import type { OrderKind, UasClass, UasClassProfile } from '../types'
import { routeWidthM } from './geo'

/* ============================================================================
 * ПРАВИЛА КЛАССОВ БВС (РОССИЙСКАЯ КЛАССИФИКАЦИЯ)
 *
 * Применяются классы Н, A, C, G, H.
 * Западная классификация B / D / E НЕ ИСПОЛЬЗУЕТСЯ.
 *
 * Ключевые требования ТЗ:
 *  • класс «Н»: 0–150 м, ≤ 30 кг, VLOS, УВЕДОМИТЕЛЬНЫЙ порядок;
 *  • ЭРА-ГЛОНАСС Remote ID: 1090 МГц, мощность ≥ 2 Вт, WGS-84;
 *  • ширина маршрута БВС в классах H и G — НЕ БОЛЕЕ 4 км (валидация + предупреждение);
 *  • у границ и в контролируемых зонах — РАЗРЕШИТЕЛЬНЫЙ порядок.
 * ========================================================================= */

/** Канонический порядок класса. 4000 м = 4 км. */
export const ROUTE_WIDTH_LIMIT_M = 4000

export const UAS_CLASSES: Record<UasClass, UasClassProfile> = {
  Н: {
    id: 'Н',
    label: 'Малый БВС · VLOS · уведомительный',
    minAltM: 0,
    maxAltM: 150,
    maxMassKg: 30,
    los: 'VLOS',
    order: 'notification',
    maxRouteWidthM: null,
    remoteIdRequired: true,
  },
  A: {
    id: 'A',
    label: 'Малый БВС · VLOS',
    minAltM: 0,
    maxAltM: 120,
    maxMassKg: 30,
    los: 'VLOS',
    order: 'notification',
    maxRouteWidthM: ROUTE_WIDTH_LIMIT_M,
    remoteIdRequired: true,
  },
  C: {
    id: 'C',
    label: 'Средний БВС · EVLOS',
    minAltM: 0,
    maxAltM: 120,
    maxMassKg: 150,
    los: 'EVLOS',
    order: 'notification',
    maxRouteWidthM: ROUTE_WIDTH_LIMIT_M,
    remoteIdRequired: true,
  },
  G: {
    id: 'G',
    label: 'Средний БВС · EVLOS · маршрут ≤ 4 км',
    minAltM: 0,
    maxAltM: 120,
    maxMassKg: 150,
    los: 'EVLOS',
    order: 'notification',
    maxRouteWidthM: ROUTE_WIDTH_LIMIT_M,
    remoteIdRequired: true,
  },
  H: {
    id: 'H',
    label: 'БВС вне визуальной видимости · разрешительный · маршрут ≤ 4 км',
    minAltM: 0,
    maxAltM: 150,
    maxMassKg: 150,
    los: 'EVLOS',
    order: 'permission',
    maxRouteWidthM: ROUTE_WIDTH_LIMIT_M,
    remoteIdRequired: true,
  },
}

/** Список классов для выпадающих списков — в порядке возрастания ответственности */
export const UAS_CLASS_LIST: UasClass[] = ['Н', 'A', 'C', 'G', 'H']

/** Параметры Remote ID (ЭРА-ГЛОНАСС) */
export const REMOTE_ID = {
  standard: 'ЭРА-ГЛОНАСС',
  frequencyMHz: 1090,
  minPowerW: 2,
  crs: 'WGS-84',
} as const

export const ORDER_KINDS: Record<OrderKind, { label: string; description: string }> = {
  notification: {
    label: 'Уведомительный',
    description:
      'Класс «Н» и полёты вне контролируемых зон: подача уведомления в САС без ожидания разрешения.',
  },
  permission: {
    label: 'Разрешительный',
    description:
      'Вблизи государственной границы и в контролируемых зонах: предварительное разрешение (СППИ), класс «H».',
  },
}

/** Результат проверки декларации/полёта */
export interface ValidationIssue {
  level: 'error' | 'warning' | 'info'
  message: string
}

export interface DeclarationCheckInput {
  uasClass: UasClass
  altM: number
  route: Array<[number, number]>
  /** Полёт рядом с границей или в контролируемой зоне */
  nearBorderOrControlZone?: boolean
  /** Режим видимости по факту */
  los?: 'VLOS' | 'EVLOS' | 'BVLOS'
  massKg?: number
  /** Идентификация активного Remote ID */
  remoteIdEnabled?: boolean
}

/**
 * Проверка параметров полёта по требованиям ТЗ.
 * Возвращает список замечаний: ошибки блокируют, предупреждения требуют внимания.
 */
export function validateDeclaration(input: DeclarationCheckInput): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const profile = UAS_CLASSES[input.uasClass]

  // --- Ограничение высоты по классу ---
  if (input.altM < profile.minAltM || input.altM > profile.maxAltM) {
    issues.push({
      level: 'error',
      message: `Высота ${input.altM} м вне диапазона класса «${input.uasClass}» (${profile.minAltM}–${profile.maxAltM} м).`,
    })
  }

  // --- Порядок: уведомительный или разрешительный ---
  const needsPermission =
    input.nearBorderOrControlZone === true || input.uasClass === 'H' || profile.order === 'permission'
  if (needsPermission && profile.order === 'notification' && input.nearBorderOrControlZone) {
    issues.push({
      level: 'error',
      message: 'Полёт у границы или в контролируемой зоне возможен только по разрешительному порядку (СППИ).',
    })
  }
  if (needsPermission && input.uasClass !== 'H' && input.nearBorderOrControlZone) {
    issues.push({
      level: 'warning',
      message: `Класс «${input.uasClass}» — уведомительный порядок, но район у границы/в контролируемой зоне: требуется разрешение.`,
    })
  }

  // --- ШИРИНА МАРШРУТА: в классах H и G — НЕ БОЛЕЕ 4 КМ ---
  const widthLimit = profile.maxRouteWidthM
  if (widthLimit !== null && input.route.length >= 2) {
    // Измеряем ширину маршрута (geo.routeWidthM), ошибки гасим — проверка не должна падать
    const width = routeWidthSafe(input.route)
    if (width > widthLimit) {
      issues.push({
        level: input.uasClass === 'G' || input.uasClass === 'H' ? 'error' : 'warning',
        message:
          `Ширина маршрута ${(width / 1000).toFixed(2)} км превышает предел ` +
          `${(widthLimit / 1000).toFixed(0)} км для класса «${input.uasClass}».`,
      })
    } else {
      issues.push({
        level: 'info',
        message:
          `Ширина маршрута ${(width / 1000).toFixed(2)} км — в пределах ` +
          `${(widthLimit / 1000).toFixed(0)} км для класса «${input.uasClass}».`,
      })
    }
  }

  // --- Режим видимости ---
  if (input.los && input.los !== profile.los) {
    issues.push({
      level: 'warning',
      message: `Режим ${input.los} не соответствует классу «${input.uasClass}» (${profile.los}).`,
    })
  }

  // --- Масса ---
  if (input.massKg !== undefined && input.massKg > profile.maxMassKg) {
    issues.push({
      level: 'error',
      message: `Масса ${input.massKg} кг превышает предел ${profile.maxMassKg} кг для класса «${input.uasClass}».`,
    })
  }

  // --- Remote ID (ЭРА-ГЛОНАСС) ---
  if (profile.remoteIdRequired && input.remoteIdEnabled === false) {
    issues.push({
      level: 'error',
      message: `Идентификация Remote ID (${REMOTE_ID.standard}, ${REMOTE_ID.frequencyMHz} МГц) обязательна для класса «${input.uasClass}».`,
    })
  }

  // --- Маршрут ---
  if (input.route.length < 2) {
    issues.push({ level: 'warning', message: 'Маршрут не задан: укажите не менее двух точек.' })
  }

  return issues
}

/** Расчёт ширины маршрута с безопасной обработкой (обёртка над geo.routeWidthM) */
function routeWidthSafe(route: Array<[number, number]>): number {
  try {
    return routeWidthM(route)
  } catch {
    return 0
  }
}

/** Подсказка по требуемому порядку для класса и района */
export function orderHint(uasClass: UasClass, nearControlZone: boolean): OrderKind {
  const p = UAS_CLASSES[uasClass]
  return nearControlZone || p.order === 'permission' ? 'permission' : 'notification'
}
